import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(() => localStorage.getItem("cl_active_org") || "");
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setOrgs([]);
      setActiveOrgId("");
      localStorage.removeItem("cl_active_org");
      setLoaded(true);
      return;
    }
    try {
      const { data } = await api.get("/organizations");
      setOrgs(data.organizations || []);
      const stored = localStorage.getItem("cl_active_org");
      const valid = data.organizations?.some((o) => o.id === stored);
      const next = valid ? stored : data.active_org_id || data.organizations?.[0]?.id || "";
      if (next) {
        setActiveOrgId(next);
        localStorage.setItem("cl_active_org", next);
      }
    } catch (e) {
      setOrgs([]);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchOrg = (id) => {
    localStorage.setItem("cl_active_org", id);
    setActiveOrgId(id);
    // trigger data reloads
    window.dispatchEvent(new CustomEvent("cl:org-switched", { detail: { id } }));
  };

  const active = orgs.find((o) => o.id === activeOrgId);
  const role = active?.role || null;

  return (
    <OrgContext.Provider value={{ orgs, activeOrgId, active, role, switchOrg, refresh, loaded }}>
      {children}
    </OrgContext.Provider>
  );
}

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be inside OrgProvider");
  return ctx;
};
